import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import getReferenceDocs from 'src/_components/getPlayerReferenceDoc';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function PlayerReference() {
  const reference = getReferenceDocs();

  function _slugify(string) {
    return string.toLowerCase().replace(/ /g, '-');
  }

  function _normalizeType(string) {
    string = string.toLowerCase();
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const ReferenceItemMethod = (props) => {
    const item = props.item;

    return (
      <div>
        <Header size="4">Function</Header>
        <CodeBlock language="javascript">{item.function.name}</CodeBlock>

        {typeof item.function.parameters !== 'undefined' && (
          <div>
            <Header size="4">Parameters</Header>
            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Data type</th>
                  <th>Required?</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {item.function.parameters.map(function (param, iii) {
                  return (
                    <tr key={`function-params-${iii}`}>
                      <td>
                        <code>{param.parameter}</code>
                      </td>
                      <td>
                        {typeof param.type === 'string' ? _normalizeType(param.type) : param.type}
                      </td>
                      <td>{param.required ? 'Yes' : 'No'}</td>
                      <td>{param.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {typeof item.function.returns !== 'undefined' && (
          <div>
            <Header size="4">Returns</Header>
            <blockquote>
              <code>{item.function.returns}</code>
            </blockquote>
          </div>
        )}

        {typeof item.function.errors !== 'undefined' && (
          <div>
            <Header size="4">Errors</Header>
            <table>
              <thead>
                <tr>
                  <th>Error</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {item.function.errors.map(function (error, iii) {
                  return (
                    <tr key={`function-errors-${iii}`}>
                      <td>
                        <code>{error.error}</code>
                      </td>
                      <td>{error.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const ReferenceItemEvent = (props) => {
    const item = props.item;

    return (
      <div>
        <Header size="4">Event data</Header>
        {typeof item.event === 'undefined' ? (
          <Paragraph size="2">This event has no associated data.</Paragraph>
        ) : typeof item.event.data === 'undefined' ? (
          <Paragraph size="2">This event has no associated data.</Paragraph>
        ) : (
          <div>
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {item.event.data.map(function (event, iii) {
                  return (
                    <tr key={`event-data-${iii}`}>
                      <td>
                        <code>{event.property}</code>
                      </td>
                      <td>{event.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {typeof item.event !== 'undefined' && typeof item.event.sample !== 'undefined' && (
          <div>
            <Header size="4">Sample event data</Header>
            <CodeBlock language="json">{item.event.sample}</CodeBlock>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Meta
        title="Player SDK: Reference"
        description="Access comprehensive technical documentation for the methods and events supported by the Vimeo player SDK."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Link href={'/player/sdk/embed'}>Player SDK</Path.Link>
            <Path.Current>Reference</Path.Current>
          </Path>
        }
        nav={{ page: 'player-sdk', subpage: 'reference' }}
      >
        <Header>Reference</Header>
        <Paragraph size="2">
          This reference guide provides comprehensive details about the methods and events for
          controlling an embedded Vimeo player through the player SDK.
        </Paragraph>

        <Paragraph size="2">
          We've organized these methods and events under the general categories of playback
          controls, chapters, cue points, text tracks, and player details:
        </Paragraph>

        <ul>
          {Object.keys(reference).map(function (item, i) {
            return (
              <li key={`reference-toc-${i}`}>
                <Link variant="minimal" href={'#' + _slugify(item)}>
                  {item}
                </Link>
              </li>
            );
          })}
        </ul>

        <StyledNotice>
          <strong>NOTE:</strong> There are no specific events for player details and no specific
          methods for interactive videos.
        </StyledNotice>

        <Paragraph size="2">
          If you have questions or problems with the SDK, or to request new features, our{' '}
          <Link variant="minimal" href="https://github.com/vimeo/player.js">
            public repository
          </Link>{' '}
          on GitHub is the place to be. Raise a new issue and let us know.
        </Paragraph>

        <Header size="2" id="about-player-methods">
          About player methods
        </Header>
        <Paragraph size="2">
          You can call player methods by calling the corresponding function on the Player object.
        </Paragraph>

        <CodeBlock language="javascript">{`player.play();`}</CodeBlock>

        <Paragraph size="2">
          All methods, except for <code>on()</code> and <code>off()</code>, return a{' '}
          <em>Promise</em> — a special JavaScript object that stands for the result of the
          operation, whether success or failure.
        </Paragraph>

        <CodeBlock language="javascript">{`player.setAutopause(false).then(function(autopause) {
  // Autopause is disabled
}).catch(function(error) {
  switch (error.name) {
    case 'UnsupportedError':
      // Autopause isn't supported by the current player or browser
      break;

    default:
      // Some other error occurred
    break;
  }
});`}</CodeBlock>

        <Paragraph size="2">
          Promises for methods that return information resolve with the value of the property in
          question.
        </Paragraph>

        <CodeBlock language="javascript">{`player.getLoop().then(function(loop) {
  // Whether the player is set to loop
});`}</CodeBlock>

        <Paragraph size="2">
          Promises for methods that set information resolve either with the new value or an error.
        </Paragraph>

        <CodeBlock language="javascript">{`player.setColor('#00adef').then(function(color) {
  // The new color value: #00ADEF
}).catch(function(error) {
  // An error occurred while setting the color
});`}</CodeBlock>

        <Header size="2" id="about-player-events">
          About player events
        </Header>
        <Paragraph size="2">
          You can listen for events in the player by attaching a callback using <code>.on()</code>.
        </Paragraph>

        <CodeBlock language="javascript">{`player.on('ended', function(data) {
  // \`data\` is an object containing properties specific to that event
});`}</CodeBlock>

        <Paragraph size="2">
          The events are equivalent to the HTML5 video events (except for <code>cuechange</code>,
          which is slightly different).
        </Paragraph>

        <Paragraph size="2">
          To remove a listener, call <code>.off()</code> with the callback function. If you pass an
          event name only, you remove all listeners for that event.
        </Paragraph>
        <CodeBlock language="javascript">{`var onPlay = function(data) {
  // \`data\` is an object containing properties specific to that event
};

player.on('ended', onPlay);

// If later you decide that you don't need to listen for \`ended\`
player.off('ended', onPlay);

// Alternatively, call \`off\` with just the event name to remove all
// listeners
player.off('ended');`}</CodeBlock>

        {Object.keys(reference).map(function (ref, i) {
          let refType = reference[ref].type;
          let items = reference[ref].items;

          return (
            <div key={`reference-${i}`}>
              <Header size="2" id={_slugify(ref)}>
                {ref}
              </Header>
              <Paragraph size="2">Here are the {ref.toLowerCase()}:</Paragraph>

              <ul>
                {Object.keys(items).map(function (item, ii) {
                  item = items[item];

                  return (
                    <li key={`reference-${i}-toc-${ii}`}>
                      <Link variant="minimal" href={'#' + _slugify(item.name)}>
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {Object.keys(items).map(function (item, ii) {
                item = items[item];

                return (
                  <div key={`reference-${i}-item-${ii}`}>
                    <Header size="3" id={_slugify(item.name)}>
                      {item.name}
                    </Header>
                    {item.description}

                    {typeof item.note !== 'undefined' && (
                      <StyledNotice>
                        <strong>NOTE:</strong> {item.note}
                      </StyledNotice>
                    )}

                    {refType === 'method' ? (
                      <ReferenceItemMethod key={`reference-${i}-item-${ii}-method`} item={item} />
                    ) : (
                      <ReferenceItemEvent key={`reference-${i}-item-${ii}-event`} item={item} />
                    )}

                    {typeof item.example !== 'undefined' && (
                      <div>
                        <Header size="4">Example</Header>
                        {item.example}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </GuideWrapper>
    </>
  );
}
